var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useSupportStore } from "src/labs/support-v2/store";
import { useClientStore } from ".";
import { useClientPropertyStore } from "../client-property";
import { useAsyncPrimer, useCompositeAsyncPrimer, } from "../common/async-primer";
import { computed } from "vue";
import { useUser } from "../user/composables";
import Session from "js/Session";
import { useAsyncState, until } from "@vueuse/core";
import { getPage } from "../common/pagination";
import { useFuzzySearch } from "../common/fuzzy-search";
import { useSorted } from "@vueuse/core";
import { useClient } from "./composable";
export const useSupportInfo = () => {
    const supportStore = useSupportStore();
    const primer = useAsyncPrimer(() => __awaiter(void 0, void 0, void 0, function* () {
        yield supportStore.fetchSupport();
    }), "useSupportInfo");
    const supportInfo = computed(() => supportStore.getSupport());
    return {
        supportInfo,
        primer,
    };
};
export const useExtendedSupportInfo = () => {
    const { supportInfo, primer } = useSupportInfo();
    const extendedSupportInfo = useAsyncState(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f;
        yield until(() => primer.isReady).toBe(true);
        const { user: csmUser, primer: csmUserPrimer } = useUser((_a = supportInfo.value) === null || _a === void 0 ? void 0 : _a.csm_user_id);
        const { user: supportUser, primer: supportUserPrimer } = useUser((_b = supportInfo.value) === null || _b === void 0 ? void 0 : _b.support_user_id);
        yield until(() => csmUserPrimer.isReady).toBe(true);
        yield until(() => supportUserPrimer.isReady).toBe(true);
        return Object.assign(Object.assign({}, supportInfo.value), { csm_user_name: ((_c = csmUser.value) === null || _c === void 0 ? void 0 : _c.getFullName()) || undefined, csm_user_email: (_d = csmUser.value) === null || _d === void 0 ? void 0 : _d.email, support_user_name: ((_e = supportUser.value) === null || _e === void 0 ? void 0 : _e.getFullName()) || undefined, support_user_email: (_f = supportUser.value) === null || _f === void 0 ? void 0 : _f.email });
    }), null).state;
    return {
        extendedSupportInfo,
        primer,
    };
};
export const useClients = () => {
    const clientStore = useClientStore();
    const primer = useAsyncPrimer(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!clientStore.getClients().length) {
            yield clientStore.fetchClients();
        }
    }), `useClients`);
    const clients = computed(() => {
        return clientStore.getClients();
    });
    return {
        primer,
        clients,
    };
};
export const useClientDetails = (clientId) => {
    const supportStore = useSupportStore();
    const clientPropertyStore = useClientPropertyStore();
    const { client, primer: clientPrimer } = useClient(clientId);
    const primer = useAsyncPrimer(() => __awaiter(void 0, void 0, void 0, function* () {
        yield Promise.allSettled([
            supportStore.fetchSupport(),
            clientPropertyStore.fetchProperties(),
        ]);
    }), `client-${clientId}`);
    const compositePrimer = useCompositeAsyncPrimer(clientPrimer, primer);
    const clientDetails = computed(() => {
        var _a, _b, _c, _d, _e;
        const clientValue = client.value;
        const supportValue = supportStore.getSupport();
        const vertical = clientPropertyStore.getProperty("vertical");
        const mrr = clientPropertyStore.getProperty("mrr");
        const riskCategory = clientPropertyStore.getProperty("risk_category");
        const atRisk = clientPropertyStore.getProperty("at_risk");
        const usOnlySupport = ((_a = clientPropertyStore.getProperty("us_only_support")) === null || _a === void 0 ? void 0 : _a.value) ===
            "true";
        const weekly_ntf_customers = clientPropertyStore.getProperty("weekly_ntf_customers");
        const clientPriority = clientPropertyStore.getProperty("client_priority");
        const nvp = clientPropertyStore.getProperty("nvp");
        const nextRenewalDate = clientPropertyStore.getProperty("next_renewal_date");
        const initialLaunchDate = clientPropertyStore.getProperty("initial_launch_date");
        const ssd = clientPropertyStore.getProperty("ssd");
        const parentOrgName = clientPropertyStore.getProperty("parent_org_name");
        const supportOnlyServices = clientPropertyStore.getProperty("support_only_services");
        const customerJourneyStage = clientPropertyStore.getProperty("customer_journey_stage");
        const clientTenure = clientPropertyStore.getProperty("client_tenure");
        const clientDocLink = clientPropertyStore.getProperty("client_doc_link");
        const figmaLink = clientPropertyStore.getProperty("figma_link");
        const { user: csmUser } = useUser(supportValue && supportValue.csm_user_id
            ? supportValue.csm_user_id
            : "");
        const { user: supportUser } = useUser(supportValue && supportValue.support_user_id
            ? supportValue.support_user_id
            : "");
        return {
            client: clientValue,
            support: {
                slack_channel_name: supportValue === null || supportValue === void 0 ? void 0 : supportValue.slack_channel_name,
                external_slack_channel_name: supportValue === null || supportValue === void 0 ? void 0 : supportValue.external_slack_channel_name,
                salesforce_account_id: supportValue === null || supportValue === void 0 ? void 0 : supportValue.salesforce_account_id,
                notes: supportValue === null || supportValue === void 0 ? void 0 : supportValue.notes,
                csm_user_id: supportValue === null || supportValue === void 0 ? void 0 : supportValue.csm_user_id,
                csm_user_name: ((_b = csmUser.value) === null || _b === void 0 ? void 0 : _b.getFullName()) || undefined,
                csm_user_email: (_c = csmUser.value) === null || _c === void 0 ? void 0 : _c.email,
                support_user_id: supportValue === null || supportValue === void 0 ? void 0 : supportValue.support_user_id,
                support_user_name: ((_d = supportUser.value) === null || _d === void 0 ? void 0 : _d.getFullName()) || undefined,
                support_user_email: (_e = supportUser.value) === null || _e === void 0 ? void 0 : _e.email,
            },
            vertical: vertical === null || vertical === void 0 ? void 0 : vertical.value,
            mrr: mrr === null || mrr === void 0 ? void 0 : mrr.value,
            risk_category: riskCategory === null || riskCategory === void 0 ? void 0 : riskCategory.value,
            at_risk: atRisk === null || atRisk === void 0 ? void 0 : atRisk.value,
            us_only_support: usOnlySupport,
            weekly_ntf_customers: weekly_ntf_customers === null || weekly_ntf_customers === void 0 ? void 0 : weekly_ntf_customers.value,
            client_priority: clientPriority === null || clientPriority === void 0 ? void 0 : clientPriority.value,
            nvp: nvp === null || nvp === void 0 ? void 0 : nvp.value,
            next_renewal_date: (nextRenewalDate === null || nextRenewalDate === void 0 ? void 0 : nextRenewalDate.value) || null,
            initial_launch_date: (initialLaunchDate === null || initialLaunchDate === void 0 ? void 0 : initialLaunchDate.value) || null,
            ssd: (ssd === null || ssd === void 0 ? void 0 : ssd.value) || null,
            parent_org_name: parentOrgName === null || parentOrgName === void 0 ? void 0 : parentOrgName.value,
            support_only_services: supportOnlyServices === null || supportOnlyServices === void 0 ? void 0 : supportOnlyServices.value,
            customer_journey_stage: customerJourneyStage === null || customerJourneyStage === void 0 ? void 0 : customerJourneyStage.value,
            client_tenure: clientTenure === null || clientTenure === void 0 ? void 0 : clientTenure.value,
            client_doc_link: clientDocLink === null || clientDocLink === void 0 ? void 0 : clientDocLink.value,
            figma_link: figmaLink === null || figmaLink === void 0 ? void 0 : figmaLink.value,
        };
    });
    return {
        clientDetails,
        primer: compositePrimer,
    };
};
export const useMeClientDetails = () => {
    const meClientId = Session.getInstance().getClientId();
    return useClientDetails(meClientId);
};
export const useFilteredClients = (parameters, paginationParams) => {
    const { clients, primer } = useClients();
    const sortedByName = useSorted(clients, (item1, item2) => item1.name.toLowerCase().localeCompare(item2.name.toLowerCase()));
    const searchedClients = useFuzzySearch(parameters.search, sortedByName, ["name", "client_id"], {
        exactMatch: true,
        shouldSearchAnywhere: true,
        shouldSort: false,
    });
    return computed(() => {
        return getPage(searchedClients.value, paginationParams.parameters.value);
    });
};
